import { useEffect, useState } from 'react';
import { Download, Pencil, Trash2, Upload } from 'lucide-react';
import { projectsApi, releasesApi, type ImageInspection, type Project, type Release, type ReleaseStatus } from '../api';
import { useAsync, useProjectSelection } from '../hooks';
import ProjectPicker from '../components/ProjectPicker';
import { ReleaseStatusBadge } from '../components/StatusBadges';
import { Alert, Button, Card, Empty, Field, Modal, PageHeader, Select, TextArea, errorText, formatBytes, formatDate } from '../components/ui';

// Must match VERSION_PATTERN in backend/app/schemas.py.
const VERSION_RE = /^[A-Za-z0-9][A-Za-z0-9._+-]{0,63}$/;
const VERSION_HELP = "Letters, digits, '.', '_', '+', '-' (e.g. 1.9). Must equal the version string the firmware reports.";

export default function ReleasesPage() {
  const { projects, list, selected, select } = useProjectSelection();
  const releases = useAsync(() => (selected ? releasesApi.list(selected.id) : Promise.resolve([])), [selected?.id]);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState<Release | null>(null);
  const [notice, setNotice] = useState<{ kind: 'error' | 'warning' | 'success'; text: string } | null>(null);

  if (projects.error) return <Alert kind="error">{projects.error}</Alert>;
  if (!projects.loading && !selected) return <Empty>No projects available.</Empty>;

  const reloadAll = async () => {
    await Promise.all([releases.reload(), projects.reload()]);
  };

  const act = async (action: () => Promise<unknown>, success?: string) => {
    setNotice(null);
    try {
      await action();
      if (success) setNotice({ kind: 'success', text: success });
      await reloadAll();
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err) });
    }
  };

  return (
    <div>
      <PageHeader title="Releases">
        <ProjectPicker projects={list} value={String(selected?.id ?? '')} onChange={select} />
        <Button onClick={() => setUploading(true)} disabled={!selected}>
          <Upload size={16} /> Upload release
        </Button>
      </PageHeader>

      {notice && (
        <div className="mb-4">
          <Alert kind={notice.kind}>{notice.text}</Alert>
        </div>
      )}

      {selected && <GroupDefaults project={selected} releases={releases.data ?? []} onChanged={act} />}

      {releases.error && <Alert kind="error">{releases.error}</Alert>}
      {!releases.loading && releases.data?.length === 0 && <Empty>No releases yet. Upload the first binary.</Empty>}
      <div className="space-y-3">
        {(releases.data ?? []).map((r) => (
          <ReleaseCard
            key={r.id}
            release={r}
            onEdit={() => setEditing(r)}
            onStatus={(status) => act(() => releasesApi.update(r.id, { status }), `Release ${r.version} is now ${status}.`)}
            onDelete={() =>
              confirm(`Delete release ${r.version} and its binary?`) && act(() => releasesApi.remove(r.id), `Release ${r.version} deleted.`)
            }
          />
        ))}
      </div>

      {uploading && selected && (
        <UploadModal
          project={selected}
          onClose={() => setUploading(false)}
          onUploaded={(release) => {
            setUploading(false);
            setNotice(
              release.warnings.length
                ? { kind: 'warning', text: `Release ${release.version} uploaded with warnings: ${release.warnings.join(' ')}` }
                : { kind: 'success', text: `Release ${release.version} uploaded.` },
            );
            reloadAll();
          }}
        />
      )}
      {editing && (
        <EditModal
          release={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reloadAll();
          }}
        />
      )}
    </div>
  );
}

function GroupDefaults({
  project,
  releases,
  onChanged,
}: {
  project: Project;
  releases: Release[];
  onChanged: (action: () => Promise<unknown>, success?: string) => void;
}) {
  const usable = releases.filter((r) => r.status !== 'blocked');
  return (
    <Card className="mb-4 p-4">
      <h2 className="mb-1 text-sm font-medium">Group default releases</h2>
      <p className="mb-3 text-xs text-slate-500">
        Devices without an assigned release install their group's default. New devices join the default group:{' '}
        {project.groups.find((g) => g.id === project.default_group_id)?.name ?? '—'}.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {project.groups.map((g) => (
          <Select
            key={g.id}
            label={`${g.name} (${g.device_count} devices)`}
            value={g.default_release_id ?? ''}
            onChange={(e) =>
              onChanged(
                () => projectsApi.updateGroup(g.id, { default_release_id: e.target.value ? Number(e.target.value) : null }),
                `Default release of ${g.name} updated.`,
              )
            }
          >
            <option value="">— none —</option>
            {usable.map((r) => (
              <option key={r.id} value={r.id}>
                {r.version}
                {r.label ? ` – ${r.label}` : ''}
              </option>
            ))}
          </Select>
        ))}
      </div>
    </Card>
  );
}

function ReleaseCard({
  release: r,
  onEdit,
  onStatus,
  onDelete,
}: {
  release: Release;
  onEdit: () => void;
  onStatus: (status: ReleaseStatus) => void;
  onDelete: () => void;
}) {
  const [showChangelog, setShowChangelog] = useState(false);
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-lg font-semibold">{r.version}</span>
            {r.label && <span className="text-slate-600">{r.label}</span>}
            <ReleaseStatusBadge status={r.status} />
          </div>
          {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
          <p className="mt-1 text-xs text-slate-500">
            Uploaded {formatDate(r.created_at)} · {formatBytes(r.size)} · sha256 <span className="font-mono">{r.sha256.slice(0, 12)}…</span>
            {r.image_version && (
              <>
                {' '}
                · image {r.image_project} <span className="font-mono">{r.image_version}</span> built {r.image_build_date}
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select aria-label="Status" value={r.status} onChange={(e) => onStatus(e.target.value as ReleaseStatus)}>
            <option value="active">active</option>
            <option value="deprecated">deprecated</option>
            <option value="blocked">blocked</option>
          </Select>
          <a href={releasesApi.downloadUrl(r.id)} className="rounded-md border border-slate-300 p-2 text-slate-600 hover:bg-slate-50" title="Download">
            <Download size={16} />
          </a>
          <Button variant="secondary" onClick={onEdit} aria-label="Edit">
            <Pencil size={16} />
          </Button>
          <Button variant="danger" onClick={onDelete} aria-label="Delete">
            <Trash2 size={16} />
          </Button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-sm">
        <span>
          <span className="font-medium">{r.target_count}</span> <span className="text-slate-500">targeting</span>
        </span>
        <span>
          <span className="font-medium">{r.installed_count}</span> <span className="text-slate-500">installed</span>
        </span>
        <span className={r.unhealthy_count ? 'text-amber-700' : ''}>
          <span className="font-medium">{r.unhealthy_count}</span> <span className={r.unhealthy_count ? '' : 'text-slate-500'}>unstable</span>
        </span>
        <button type="button" className="text-blue-600 hover:underline" onClick={() => setShowChangelog(!showChangelog)}>
          {showChangelog ? 'Hide changelog' : 'Show changelog'}
        </button>
      </div>
      {showChangelog && <pre className="mt-2 rounded-md bg-slate-50 p-3 text-xs whitespace-pre-wrap text-slate-700">{r.changelog}</pre>}
      {r.status === 'blocked' && <p className="mt-2 text-xs text-red-700">Blocked: never offered or served to devices.</p>}
      {r.status === 'deprecated' && <p className="mt-2 text-xs text-amber-700">Deprecated: not available for new updates; devices already targeting it keep it.</p>}
    </Card>
  );
}

function UploadModal({ project, onClose, onUploaded }: { project: Project; onClose: () => void; onUploaded: (r: Release) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [changelog, setChangelog] = useState('');
  const [inspection, setInspection] = useState<ImageInspection | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const versionError = version && !VERSION_RE.test(version) ? `Invalid version name. ${VERSION_HELP}` : null;

  // Ask the server to check the binary (and version) whenever either changes.
  useEffect(() => {
    if (!file) {
      setInspection(null);
      return;
    }
    let cancelled = false;
    setChecking(true);
    const timer = setTimeout(() => {
      releasesApi
        .inspect(project.id, file, version.trim())
        .then((result) => !cancelled && setInspection(result))
        .catch((err) => !cancelled && setError(errorText(err)))
        .finally(() => !cancelled && setChecking(false));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [file, version, project.id]);

  const blocking = !!versionError || !!inspection?.errors.length;
  const canSubmit = file && version.trim() && changelog.trim() && !blocking && !checking && !saving;

  const submit = async () => {
    if (!file) return;
    setSaving(true);
    setError(null);
    try {
      onUploaded(await releasesApi.upload(project.id, file, { version: version.trim(), label, description, changelog }));
    } catch (err) {
      setError(errorText(err));
      setSaving(false);
    }
  };

  return (
    <Modal title={`Upload release – ${project.name}`} onClose={onClose} wide>
      <div className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Firmware binary (.bin)</span>
          <input
            type="file"
            accept=".bin,application/octet-stream"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm hover:file:bg-slate-200"
          />
        </label>

        {inspection && (
          <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-700">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
              <span>Chip: {inspection.chip ?? '—'}</span>
              <span>Image project: {inspection.image_project ?? '—'}</span>
              <span>
                Image version: <span className="font-mono">{inspection.image_version ?? '—'}</span>
              </span>
              <span>Built: {inspection.image_build_date ?? '—'}</span>
              <span>IDF: {inspection.image_idf_version ?? '—'}</span>
            </div>
          </div>
        )}
        {inspection?.errors.map((e) => (
          <Alert key={e} kind="error">
            {e}
          </Alert>
        ))}
        {inspection?.warnings.map((w) => (
          <Alert key={w} kind="warning">
            {w}
          </Alert>
        ))}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Version" required value={version} onChange={(e) => setVersion(e.target.value)} placeholder="e.g. 1.9" hint={versionError ?? VERSION_HELP} aria-invalid={!!versionError} />
          <Field label="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Stable" />
        </div>
        <Field label="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
        <TextArea label="Changelog" required value={changelog} onChange={(e) => setChangelog(e.target.value)} hint="Required. What changed in this version?" />

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {saving ? 'Uploading…' : checking ? 'Checking…' : 'Upload'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function EditModal({ release, onClose, onSaved }: { release: Release; onClose: () => void; onSaved: () => void }) {
  const [label, setLabel] = useState(release.label);
  const [description, setDescription] = useState(release.description);
  const [changelog, setChangelog] = useState(release.changelog);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    try {
      await releasesApi.update(release.id, { label, description, changelog });
      onSaved();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <Modal title={`Edit release ${release.version}`} onClose={onClose}>
      <div className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}
        <Field label="Label" value={label} onChange={(e) => setLabel(e.target.value)} />
        <Field label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
        <TextArea label="Changelog" required value={changelog} onChange={(e) => setChangelog(e.target.value)} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!changelog.trim()}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}
